// src/components/CompanyPolicyForm.tsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Bold, Italic, Heading, List, Link2, Eye, Pencil, Check } from 'lucide-react';
import { CompanyPolicy, POLICY_CATEGORIES } from '../types/companyPolicy';
import { PolicyMarkdown } from './PolicyMarkdown';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<CompanyPolicy>) => void;
  initialData?: CompanyPolicy | null;
  isLoading?: boolean;
}

export function CompanyPolicyForm({ isOpen, onClose, onSubmit, initialData, isLoading }: Props) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('General');
  const [content, setContent] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setCategory(initialData.category || 'General');
      setContent(initialData.content || '');
      setIsActive(initialData.is_active !== false);
    } else {
      setTitle('');
      setCategory('General');
      setContent('');
      setIsActive(true);
    }
    setMode('write');
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  // Wrap the current selection with markdown markers.
  const wrap = (before: string, after = before, placeholder = 'text') => {
    const ta = taRef.current;
    if (!ta) return;
    const s = ta.selectionStart;
    const e = ta.selectionEnd;
    const sel = content.slice(s, e) || placeholder;
    const next = content.slice(0, s) + before + sel + after + content.slice(e);
    setContent(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = s + before.length;
      ta.selectionEnd = s + before.length + sel.length;
    });
  };

  // Add a prefix to the start of the current line (headings, list items).
  const prefixLine = (prefix: string) => {
    const ta = taRef.current;
    if (!ta) return;
    const s = ta.selectionStart;
    const lineStart = content.lastIndexOf('\n', s - 1) + 1;
    const next = content.slice(0, lineStart) + prefix + content.slice(lineStart);
    setContent(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = s + prefix.length;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    onSubmit({ title: title.trim(), category: category.trim() || 'General', content, is_active: isActive });
  };

  const tools = [
    { icon: Bold, label: 'Bold', run: () => wrap('**') },
    { icon: Italic, label: 'Italic', run: () => wrap('*') },
    { icon: Heading, label: 'Heading', run: () => prefixLine('## ') },
    { icon: List, label: 'Bullet list', run: () => prefixLine('- ') },
    { icon: Link2, label: 'Link', run: () => wrap('[', '](https://)', 'label') },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0f2d1f]/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col border border-[#e8e3d8]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e8e3d8] bg-[#faf8f3]">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-[#e8efe9] text-[#0f2d1f] flex items-center justify-center">
              <Pencil className="w-4 h-4" />
            </span>
            <h3 className="text-[17px] font-semibold text-[#1a1a1a]">
              {initialData ? 'Edit policy' : 'New policy'}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 text-[#8a8580] hover:text-[#1a1a1a] hover:bg-[#f4f1ea] rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Title + Category */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-[#8a8580] mb-1.5">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                autoFocus
                placeholder="e.g. Leave policy"
                className="w-full px-3.5 py-2.5 text-[14px] text-[#1a1a1a] bg-white border border-[#d6d0c2] rounded-lg outline-none focus:border-[#0f2d1f] focus:ring-2 focus:ring-[#0f2d1f]/10 transition"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-[#8a8580] mb-1.5">Category</label>
              <input
                type="text"
                list="cp-categories"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Human Resources"
                className="w-full px-3.5 py-2.5 text-[14px] text-[#1a1a1a] bg-white border border-[#d6d0c2] rounded-lg outline-none focus:border-[#0f2d1f] focus:ring-2 focus:ring-[#0f2d1f]/10 transition"
              />
              <datalist id="cp-categories">
                {POLICY_CATEGORIES.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          {/* Content editor */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-[#8a8580]">Content</label>
              <div className="flex items-center gap-1 p-0.5 bg-[#f4f1ea] rounded-lg">
                <button type="button" onClick={() => setMode('write')}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition ${mode === 'write' ? 'bg-white text-[#0f2d1f] shadow-sm' : 'text-[#8a8580] hover:text-[#1a1a1a]'}`}>
                  <Pencil className="w-3.5 h-3.5" /> Write
                </button>
                <button type="button" onClick={() => setMode('preview')}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition ${mode === 'preview' ? 'bg-white text-[#0f2d1f] shadow-sm' : 'text-[#8a8580] hover:text-[#1a1a1a]'}`}>
                  <Eye className="w-3.5 h-3.5" /> Preview
                </button>
              </div>
            </div>

            <div className="border border-[#d6d0c2] rounded-lg overflow-hidden focus-within:border-[#0f2d1f] focus-within:ring-2 focus-within:ring-[#0f2d1f]/10 transition">
              {mode === 'write' ? (
                <>
                  <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-[#e8e3d8] bg-[#faf8f3]">
                    {tools.map((t) => (
                      <button key={t.label} type="button" title={t.label} onClick={t.run}
                        className="w-8 h-8 flex items-center justify-center text-[#4a4a4a] hover:bg-[#e8efe9] hover:text-[#0f2d1f] rounded-md transition">
                        <t.icon className="w-4 h-4" />
                      </button>
                    ))}
                    <span className="ml-auto pr-2 text-[11px] text-[#b8b3ac]">Markdown supported</span>
                  </div>
                  <textarea
                    ref={taRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    required
                    rows={12}
                    placeholder={'## Section heading\nWrite the policy here. Use **bold**, *italic*, and:\n\n- bullet points\n- for clarity'}
                    className="w-full px-3.5 py-3 text-[13.5px] leading-relaxed text-[#1a1a1a] font-mono outline-none resize-y min-h-[220px]"
                  />
                </>
              ) : (
                <div className="px-4 py-3.5 min-h-[260px] bg-white">
                  {content.trim()
                    ? <PolicyMarkdown content={content} />
                    : <p className="text-sm text-[#b8b3ac] italic">Nothing to preview yet.</p>}
                </div>
              )}
            </div>
          </div>

          {/* Published toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <button type="button" role="switch" aria-checked={isActive} onClick={() => setIsActive((v) => !v)}
              className={`relative w-10 h-6 rounded-full transition-colors ${isActive ? 'bg-[#0f2d1f]' : 'bg-[#d6d0c2]'}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-4' : ''}`} />
            </button>
            <span className="text-[13px] text-[#4a4a4a]">
              <span className="font-medium text-[#1a1a1a]">{isActive ? 'Published' : 'Draft'}</span>
              {' — '}{isActive ? 'visible to all employees' : 'hidden from employees (only you can see it)'}
            </span>
          </label>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-[#e8e3d8] bg-[#faf8f3]">
          <button type="button" onClick={onClose}
            className="px-4 py-2.5 text-[13.5px] font-medium text-[#4a4a4a] border border-[#d6d0c2] rounded-lg hover:bg-white transition">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={isLoading || !title.trim() || !content.trim()}
            className="flex items-center gap-1.5 px-5 py-2.5 text-[13.5px] font-medium text-white bg-[#0f2d1f] rounded-lg hover:bg-[#0a1f15] disabled:opacity-50 disabled:cursor-not-allowed transition">
            <Check className="w-4 h-4" />
            {isLoading ? 'Saving…' : initialData ? 'Save changes' : 'Create policy'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompanyPolicyForm;
